import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/shared/store/Store';
import type { GroupingFamily, TimeBucketGranularity } from '@/types/SummaryReport';

// UC-82 alias kept for backward compat; both names refer to the same type
export type BillableStatus = 'all' | 'billable-only' | 'non-billable-only';
export type ReportBillableStatus = BillableStatus;

export type GroupingDimension = 'time-bucket' | 'user' | 'client' | 'project' | 'tag' | 'billable';
export type ReportsTab = 'summary' | 'detail';
export type ExportFormat = 'xlsx' | 'csv';

export type ReportsFilters = {
  fromDate: string;
  toDate: string;
  userIds: string[];
  projectIds: string[];
  clientIds: string[];
  billableStatus: BillableStatus;
  tagIds: string[];
  descriptionSearch: string;
};

export type ReportSort = {
  field: string;
  direction: 'asc' | 'desc';
};

export type ReportsPageState = {
  activeTab: ReportsTab;
  filters: ReportsFilters;
  grouping: GroupingFamily;
  timeBucketGranularity: TimeBucketGranularity;
  sort: ReportSort;
  exportModalOpen: boolean;
  exportDraft: { format: ExportFormat; includeDetailRows: boolean };
  exportInProgress: boolean;
  submitInProgress: boolean;
  lastErrorKey: string | null;
};

const initialState: ReportsPageState = {
  activeTab: 'summary',
  filters: {
    fromDate: '',
    toDate: '',
    userIds: [],
    projectIds: [],
    clientIds: [],
    billableStatus: 'all',
    tagIds: [],
    descriptionSearch: '',
  },
  grouping: 'time-bucket',
  timeBucketGranularity: 'week',
  sort: { field: 'startedAt', direction: 'asc' },
  exportModalOpen: false,
  exportDraft: { format: 'xlsx', includeDetailRows: false },
  exportInProgress: false,
  submitInProgress: false,
  lastErrorKey: null,
};

export const reportsPageSlice = createSlice({
  name: 'reportsPage',
  initialState,
  reducers: {
    setReportsActiveTab(state, action: PayloadAction<ReportsTab>) {
      state.activeTab = action.payload;
    },

    // UC-81 — flat single-field date setters (used by the sidebar inputs)
    setReportsFromDate(state, action: PayloadAction<string>) {
      state.filters.fromDate = action.payload;
      state.filters.tagIds = [];
    },
    setReportsToDate(state, action: PayloadAction<string>) {
      state.filters.toDate = action.payload;
    },

    // UC-82 — sets both date fields atomically (used by tests and legacy callers)
    setReportsDateRange(state, action: PayloadAction<{ from: string; to: string }>) {
      state.filters.fromDate = action.payload.from;
      state.filters.toDate = action.payload.to;
    },

    // Initialises date range only when fields are still empty (called on page mount)
    initReportsDateRange(state, action: PayloadAction<{ fromDate: string; toDate: string }>) {
      if (!state.filters.fromDate) state.filters.fromDate = action.payload.fromDate;
      if (!state.filters.toDate) state.filters.toDate = action.payload.toDate;
    },

    // Project filter — dual naming to satisfy UC-81 and UC-82 callers
    setFilterProjectIds(state, action: PayloadAction<string[]>) {
      state.filters.projectIds = action.payload;
      state.filters.tagIds = [];
    },
    setFilterUserIds(state, action: PayloadAction<string[]>) {
      state.filters.userIds = action.payload;
    },
    setReportsFilterProjectIds(state, action: PayloadAction<string[]>) {
      state.filters.projectIds = action.payload;
      state.filters.tagIds = [];
    },

    // Client filter
    setFilterClientIds(state, action: PayloadAction<string[]>) {
      state.filters.clientIds = action.payload;
    },
    setReportsFilterClientIds(state, action: PayloadAction<string[]>) {
      state.filters.clientIds = action.payload;
    },

    // Billable filter
    setFilterBillableStatus(state, action: PayloadAction<BillableStatus>) {
      state.filters.billableStatus = action.payload;
    },
    setReportsFilterBillableStatus(state, action: PayloadAction<BillableStatus>) {
      state.filters.billableStatus = action.payload;
    },

    // Tag filter
    setFilterTagIds(state, action: PayloadAction<string[]>) {
      state.filters.tagIds = action.payload;
    },
    setReportsFilterTagIds(state, action: PayloadAction<string[]>) {
      state.filters.tagIds = action.payload;
    },

    // UC-82 additions
    setReportsFilterUserIds(state, action: PayloadAction<string[]>) {
      state.filters.userIds = action.payload;
    },
    setReportsDescriptionSearch(state, action: PayloadAction<string>) {
      state.filters.descriptionSearch = action.payload;
    },

    // Grouping / granularity / sort
    setReportsGrouping(state, action: PayloadAction<GroupingFamily>) {
      state.grouping = action.payload;
    },
    setReportsTimeBucketGranularity(state, action: PayloadAction<TimeBucketGranularity>) {
      state.timeBucketGranularity = action.payload;
    },
    setReportsSort(state, action: PayloadAction<ReportSort>) {
      state.sort = action.payload;
    },

    // Export / status (UC-82, reserved for UC-83)
    setReportsExportModalOpen(state, action: PayloadAction<boolean>) {
      state.exportModalOpen = action.payload;
    },
    setReportsExportDraft(
      state,
      action: PayloadAction<{ format: ExportFormat; includeDetailRows: boolean }>,
    ) {
      state.exportDraft = action.payload;
    },
    setReportsExportInProgress(state, action: PayloadAction<boolean>) {
      state.exportInProgress = action.payload;
    },
    setReportsSubmitInProgress(state, action: PayloadAction<boolean>) {
      state.submitInProgress = action.payload;
    },
    setReportsLastErrorKey(state, action: PayloadAction<string | null>) {
      state.lastErrorKey = action.payload;
    },
  },
});

export const {
  setReportsActiveTab,
  setReportsFromDate,
  setReportsToDate,
  setFilterUserIds,
  setReportsDateRange,
  initReportsDateRange,
  setFilterProjectIds,
  setReportsFilterProjectIds,
  setFilterClientIds,
  setReportsFilterClientIds,
  setFilterBillableStatus,
  setReportsFilterBillableStatus,
  setFilterTagIds,
  setReportsFilterTagIds,
  setReportsFilterUserIds,
  setReportsDescriptionSearch,
  setReportsGrouping,
  setReportsTimeBucketGranularity,
  setReportsSort,
  setReportsExportModalOpen,
  setReportsExportDraft,
  setReportsExportInProgress,
  setReportsSubmitInProgress,
  setReportsLastErrorKey,
} = reportsPageSlice.actions;

export const selectReportsPage = (state: RootState) => state.reportsPage;

// Derived: true when toDate - fromDate > 365 days (strict)
export const selectReportsOver1YearMode = (state: {
  reportsPage: ReportsPageState;
}): boolean => {
  const { fromDate, toDate } = state.reportsPage.filters;
  if (!fromDate || !toDate) return false;
  const oneYearMs = 365 * 24 * 60 * 60 * 1000;
  return new Date(toDate).getTime() - new Date(fromDate).getTime() > oneYearMs;
};
